import { useEffect, useRef, useState } from 'react';
import WatchlistDashboardService, { CustomAlumniSearchResult, CustomWatchlistCardData, StandardAlumniRow } from '../WatchlistDashboardService';

/**
 * Create / Edit Custom Watchlist modal (mockup lines 542-609). Controlled (React state) overlay —
 * no Bootstrap JS dependency.
 *
 * Two ways to add alumni:
 *   - Search — type a name/email (full alumni base), click a result.
 *   - Bulk   — paste or upload a CSV of emails/names; matched are added, unmatched reported.
 *
 * A watchlist must have at least MIN_MEMBERS alumni (enforced on both create and edit).
 * Pass `editWatchlist` to open in edit mode: name / description / layer + members are pre-filled,
 * and on save the header is PATCHed and member additions/removals are diffed against the original.
 */

const MIN_MEMBERS = 5;

// Split pasted text / a CSV file into identifier tokens: one per line or comma-separated.
// Drops a leading header cell (email / name / identifier / alumni) so a real CSV export works.
const HEADER_TOKENS = new Set(['email', 'e-mail', 'email_id', 'name', 'alumni', 'alumni_name', 'identifier']);
const parseIdentifiers = (text: string): string[] => {
    const tokens = text
        .split(/[\r\n,]+/)
        .map((t) => t.trim().replace(/^["']|["']$/g, '').trim())
        .filter(Boolean);
    if (tokens.length && HEADER_TOKENS.has(tokens[0].toLowerCase())) tokens.shift();
    return tokens;
};

// A member row (standard shape) → the picker's selected shape (email unknown here → null).
const toSelected = (row: StandardAlumniRow): CustomAlumniSearchResult => ({ ...row, email: null });

const CreateWatchlistModal = ({
    instituteId,
    filterInstituteId,
    editWatchlist,
    onClose,
    onCreated,
}: {
    instituteId: string | number;
    filterInstituteId?: string | null;
    editWatchlist?: CustomWatchlistCardData;
    onClose: () => void;
    onCreated: () => void;
}) => {
    const isEdit = !!editWatchlist;
    // When a campus filter is active, scope create + search + add to that campus so the new
    // watchlist appears in the filtered list and members are drawn from the same campus.
    const effectiveInstituteId = filterInstituteId || instituteId;

    const [name, setName] = useState(editWatchlist?.name || '');
    const [layer, setLayer] = useState(editWatchlist?.layer_context || 'signal');
    const [description, setDescription] = useState(editWatchlist?.description || '');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<CustomAlumniSearchResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [searched, setSearched] = useState(false);
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [selected, setSelected] = useState<CustomAlumniSearchResult[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadingMembers, setLoadingMembers] = useState(isEdit);
    // Member ids present when an existing watchlist was opened (for add/remove diffing on save).
    const originalIds = useRef<Set<string>>(new Set());

    // Bulk add (paste / CSV)
    const [addMode, setAddMode] = useState<'search' | 'bulk'>('search');
    const [bulkText, setBulkText] = useState('');
    const [resolving, setResolving] = useState(false);
    const [unmatched, setUnmatched] = useState<string[]>([]);
    const [bulkInfo, setBulkInfo] = useState<string | null>(null);

    // Edit mode: load the current members to pre-fill the selected chips.
    useEffect(() => {
        if (!editWatchlist) return;
        setLoadingMembers(true);
        WatchlistDashboardService.getCustomMembers(instituteId, editWatchlist.id, filterInstituteId)
            .then((r) => {
                const rows = (r.alumni || []).map(toSelected);
                setSelected(rows);
                originalIds.current = new Set(rows.map((x) => x.student_id));
            })
            .catch(() => setSelected([]))
            .finally(() => setLoadingMembers(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editWatchlist?.id]);

    const mergeSelected = (rows: CustomAlumniSearchResult[]) =>
        setSelected((prev) => {
            const ids = new Set(prev.map((s) => s.student_id));
            return [...prev, ...rows.filter((r) => !ids.has(r.student_id))];
        });

    const onFile = (file: File | null) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setBulkText(String(reader.result || ''));
        reader.readAsText(file);
    };

    const resolveBulk = async () => {
        const identifiers = parseIdentifiers(bulkText);
        if (identifiers.length === 0) {
            setBulkInfo('Paste or upload at least one email or name.');
            return;
        }
        setResolving(true);
        setBulkInfo(null);
        setUnmatched([]);
        try {
            const res = await WatchlistDashboardService.resolveCustomAlumni(instituteId, identifiers, filterInstituteId);
            mergeSelected(res.matched);
            setUnmatched(res.unmatched);
            setBulkInfo(`${res.matched.length} matched · ${res.unmatched.length} not found (of ${res.requested}).`);
            if (res.matched.length > 0) setBulkText('');
        } catch (err: any) {
            setBulkInfo(err?.message || 'Failed to resolve the list.');
        } finally {
            setResolving(false);
        }
    };

    const doSearch = (q: string) => {
        const term = q.trim();
        if (term.length < 2) {
            setResults([]);
            setSearched(false);
            return;
        }
        setSearching(true);
        WatchlistDashboardService.searchCustomAlumni(instituteId, term, filterInstituteId)
            .then((r) => setResults(r.results))
            .catch(() => setResults([]))
            .finally(() => {
                setSearching(false);
                setSearched(true);
            });
    };

    // Debounced as the user types (the full alumni base is large); Enter searches immediately.
    const runSearch = (q: string) => {
        setQuery(q);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        if (q.trim().length < 2) {
            setResults([]);
            setSearched(false);
            return;
        }
        searchTimer.current = setTimeout(() => doSearch(q), 300);
    };

    const searchNow = () => {
        if (searchTimer.current) clearTimeout(searchTimer.current);
        doSearch(query);
    };

    const addSelected = (a: CustomAlumniSearchResult) => {
        if (!selected.some((s) => s.student_id === a.student_id)) setSelected([...selected, a]);
    };
    const removeSelected = (id: string) => setSelected(selected.filter((s) => s.student_id !== id));

    const save = async () => {
        if (!name.trim()) {
            setError('Watchlist name is required.');
            return;
        }
        if (selected.length < MIN_MEMBERS) {
            setError(`A watchlist needs at least ${MIN_MEMBERS} alumni — currently ${selected.length}.`);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (isEdit && editWatchlist) {
                await WatchlistDashboardService.updateCustomWatchlist(
                    instituteId,
                    editWatchlist.id,
                    { name: name.trim(), layer_context: layer, description: description.trim() },
                    filterInstituteId
                );
                // Diff members against the originally-loaded set.
                const currentIds = new Set(selected.map((s) => s.student_id));
                const toAdd = selected.filter((s) => !originalIds.current.has(s.student_id)).map((s) => s.student_id);
                const toRemove = [...originalIds.current].filter((id) => !currentIds.has(id));
                if (toAdd.length > 0) {
                    await WatchlistDashboardService.addCustomMembers(instituteId, editWatchlist.id, toAdd, filterInstituteId);
                }
                for (const id of toRemove) {
                    await WatchlistDashboardService.removeCustomMember(instituteId, editWatchlist.id, id, filterInstituteId);
                }
            } else {
                const created = await WatchlistDashboardService.createCustomWatchlist(effectiveInstituteId, {
                    name: name.trim(),
                    layer_context: layer,
                    description: description.trim(),
                });
                await WatchlistDashboardService.addCustomMembers(instituteId, created.id, selected.map((s) => s.student_id), filterInstituteId);
            }
            onCreated();
        } catch (err: any) {
            setError(err?.message || `Failed to ${isEdit ? 'update' : 'create'} watchlist.`);
            setSaving(false);
        }
    };

    const belowMin = selected.length < MIN_MEMBERS;

    return (
        <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg rounded-4">
                    <div className="modal-header px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
                        <h5 className="modal-title font-18 font-600 text-dark-blue">{isEdit ? 'Edit Custom Watchlist' : 'Create Custom Watchlist'}</h5>
                        <button type="button" className="btn-close shadow-none" onClick={onClose}></button>
                    </div>
                    <div className="modal-body p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {error && <div className="alert alert-danger font-12">{error}</div>}
                        <div className="mb-3">
                            <label className="form-label text-muted font-12 font-600">Watchlist Name *</label>
                            <input type="text" className="form-control rounded-3 font-14" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Alumni in VC / Investment Roles" />
                        </div>
                        <div className="mb-3">
                            <label className="form-label text-muted font-12 font-600">Layer Context</label>
                            <select className="form-select rounded-3 font-14" value={layer} onChange={(e) => setLayer(e.target.value)}>
                                <option value="signal">Signal Layer (visibility / understanding)</option>
                                <option value="strategic">Strategic Layer (prioritisation / focus)</option>
                            </select>
                        </div>
                        <div className="mb-3">
                            <label className="form-label text-muted font-12 font-600">Description / Criteria</label>
                            <textarea className="form-control rounded-3 h-auto font-14" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe why you are creating this watchlist…" />
                        </div>
                        <div className="mb-3">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <label className="form-label text-muted font-12 font-600 m-0">Add Alumni</label>
                                <div className="btn-group btn-group-sm" role="group">
                                    <button type="button" className={`btn ${addMode === 'search' ? 'btn-primary' : 'btn-outline-secondary'} font-11`} onClick={() => setAddMode('search')}>Search</button>
                                    <button type="button" className={`btn ${addMode === 'bulk' ? 'btn-primary' : 'btn-outline-secondary'} font-11`} onClick={() => setAddMode('bulk')}>Bulk / CSV</button>
                                </div>
                            </div>

                            {addMode === 'search' && (
                                <>
                                    <input
                                        type="text"
                                        className="form-control rounded-3 mb-2 font-14"
                                        value={query}
                                        onChange={(e) => runSearch(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchNow(); } }}
                                        placeholder="Search any alumni by name or email…"
                                    />
                                    {results.length > 0 && (
                                        <div className="border rounded-3 mb-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                                            {results.map((r) => (
                                                <div key={r.student_id} className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom cursor-pointer" onClick={() => addSelected(r)}>
                                                    <span className="font-12 font-600 text-dark">{r.name}</span>
                                                    <span className="font-11 text-muted">{r.program_and_year}{r.email ? ` · ${r.email}` : ''}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {searching && <div className="font-11 text-secondary mb-2">Searching…</div>}
                                    {!searching && searched && results.length === 0 && (
                                        <div className="font-11 text-muted mb-2">No alumni found for “{query.trim()}”.</div>
                                    )}
                                </>
                            )}

                            {addMode === 'bulk' && (
                                <div className="mb-2">
                                    <textarea
                                        className="form-control rounded-3 h-auto font-13 mb-2"
                                        rows={4}
                                        value={bulkText}
                                        onChange={(e) => setBulkText(e.target.value)}
                                        placeholder="Paste emails or names — one per line or comma-separated. A CSV with an email/name column works too."
                                    />
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <input
                                            type="file"
                                            accept=".csv,text/csv,text/plain"
                                            className="form-control form-control-sm rounded-3 font-12"
                                            onChange={(e) => { onFile(e.target.files && e.target.files[0]); e.target.value = ''; }}
                                        />
                                        <button type="button" className="btn btn-sm btn-outline-primary rounded-3 text-nowrap font-12" onClick={resolveBulk} disabled={resolving}>
                                            {resolving ? 'Resolving…' : 'Resolve & Add'}
                                        </button>
                                    </div>
                                    {bulkInfo && <div className="font-11 text-secondary mb-1">{bulkInfo}</div>}
                                    {unmatched.length > 0 && (
                                        <div className="font-11 text-danger" style={{ maxHeight: 80, overflowY: 'auto' }}>
                                            Not found: {unmatched.join(', ')}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Selected count + min-5 requirement */}
                            <div className={`font-11 mb-2 ${belowMin ? 'text-danger' : 'text-success'}`}>
                                {loadingMembers ? 'Loading members…' : `${selected.length} selected${belowMin ? ` · need at least ${MIN_MEMBERS}` : ''}`}
                            </div>

                            {selected.length > 0 && (
                                <div className="d-flex flex-wrap gap-2">
                                    {selected.map((s) => (
                                        <span key={s.student_id} className="badge bg-primary bg-opacity-10 text-primary font-11">
                                            {s.name}
                                            <i className="bi bi-x ms-1 cursor-pointer" onClick={() => removeSelected(s.student_id)}></i>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="modal-footer px-4 py-3 border-top d-flex justify-content-end gap-2">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-3" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="button" className="btn btn-primary px-4 rounded-3" onClick={save} disabled={saving || loadingMembers || belowMin}>
                            {saving ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save Changes' : 'Create Watchlist'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateWatchlistModal;
